import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import Loading from "../../common/components/Loading";
import Box from "@mui/material/Box/Box";
import UpsellingRateFormContainer from "./UpsellingRateForm/UpsellingRateFormContainer";
import UpsellingRateFilters from "./Filters/UpsellingRateFilters";
import Grid from "@mui/material/Grid/Grid";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import DialogComponent from "../../common/components/DialogComponent";
import {useRecoilState} from "recoil";
import {
    initialUpsellingRateCreation,
    upsellingRateCreationAtom,
    upsellingRateFiltersAtom,
} from "../../state/upsellingRatesRecoil";
import {useContext, useEffect, useRef, useState} from "react";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import {useUpsellingRateRepository} from "../../api/repositories/UpsellingRateRepository";
import {useUpsellingRateDetailRepository} from "../../api/repositories/UpsellingRateDetailRepository";
import {DialogInterface, SelectElementModelBE, SelectElementModelFE,} from "../../api/types/generic";
import {CommercialCategorySelectFE} from "../../api/types/commercialCategory";
import {fromSingleGenericBeToSingleGenericFe, useActionColDef} from "../../utils";
import {ColDef, ICellRendererParams} from "ag-grid-community";
import {upsellingRateColDef} from "./upsellingRateColDef";
import ActionsPopover from "../../common/components/ActionsPopover";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import dayjs from "dayjs";
import BasicToolbar from "../../common/components/BasicToolbar";
import {
    FormSearchUpsellingRateModel,
    FormUpsellingRateModel,
    UpsellingRateEntityResponse,
} from "../../api/types/upsellingRate";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";

const UpsellingRate = () => {
    // Use Recoil
    const [upsellingRatesFilters, setUpsellingRatesFilters] = useRecoilState(
        upsellingRateFiltersAtom
    );
    const [creationForm, setCreationForm] = useRecoilState(
        upsellingRateCreationAtom
    );

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use AgGridLocale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Repository Hooks
    const {
        getUpsellingRatePaged,
        deleteUpsellingRate,
        getUpsellingRateInUse,
        terminateUpsellingRate
    } = useUpsellingRateRepository();
    const {getUpsellingRateMatrix} = useUpsellingRateDetailRepository();

    // Use State
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [isCreationFormOpen, setIsCreationFormOpen] = useState(false);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    ) || [];

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchUpsellingRateModel>({
        name: "",
        commercialCategoryId: null,
        commercialSegmentId: null,
        from: "",
        to: "",
        showOnlyActive: false,
        dealers: dealerOption || [], // Usa gli oggetti invece delle stringhe
        legalEntities: legalEntityOptions // Inizializza con tutte le legal entities selezionate
    })

    // Crea una versione "pulita" di testFilters per l'hook con i codici stringa
    const cleanEntityFilters = {
        ...testFilters,
        legalEntities: Array.isArray(testFilters.legalEntities) 
            ? (testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)
            : [],
        dealers: Array.isArray(testFilters.dealers)
            ? (testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)
            : dealerFilteredOptions || [],
    };

    // Use Grid Data Custom Hook
    const {setServerSideDatasource, handleTriggerFilters, refresh} =
        useAgGridCustomHook<
            UpsellingRateEntityResponse,
            FormSearchUpsellingRateModel
        >({
            gridRef,
            api: getUpsellingRatePaged,
            initialSortColId: "name",
            entityFilters: cleanEntityFilters,
            customSort: "UpsellingRateEntityResponse",
        });

    useEffect(() => {
        if (gridRef?.current)
            handleTriggerFilters({
                name: testFilters.name,
                commercialSegmentId:
                    (
                        testFilters.commercialSegmentId as SelectElementModelFE
                    )?.code?.toString() || "",
                commercialCategoryId:
                    (
                        testFilters.commercialCategoryId as SelectElementModelFE
                    )?.code?.toString() || "",
                from: testFilters.from
                    ? dayjs(testFilters.from).toISOString()
                    : testFilters.from,
                to: testFilters.to
                    ? dayjs(testFilters.to).toISOString()
                    : testFilters.to,
                showOnlyActive: testFilters.showOnlyActive,
                legalEntities: Array.isArray(testFilters.legalEntities) 
                    ? (testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)
                    : [],
                dealers: Array.isArray(testFilters.dealers)
                    ? (testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)
                    : dealerFilteredOptions || [],
            });
    }, [testFilters.showOnlyActive]);

    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef<UpsellingRateEntityResponse>[] = upsellingRateColDef(
        {
            actionsRenderer: (params: ICellRendererParams) => (
                <ActionsPopover
                    params={params}
                    view={{
                        action: viewButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_VIEW_UPSELLING_RATE";
                            });
                        }),
                    }}
                    update={{
                        action: updateButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_UPDATE_UPSELLING_RATE";
                            });
                        }),
                    }}
                    del={{
                        action: deleteButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_DELETE_UPSELLING_RATE";
                            });
                        }),
                    }}
                    terminate={{
                        action: terminateButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_TERMINATE_UPSELLING_RATE";
                            });
                        }),
                    }}
                />
            ),
            actionColDef,
            user
        }
    );

    const defaultColDef = DEFAULT_COL_DEF;

    /* TABLE ACTIONS - START */
    const viewButtonClicked = async (data: UpsellingRateEntityResponse) => {
        setCreationForm({
            readOnly: true,
            inUse: false,
            upsellingRateId: data.id,
            definitionData: getDefinitionData(data),
            matrixData: await upsellingRateMatrix(data.id),
            step: 0,
            completed: [true, true, true],
        });
        setIsCreationFormOpen(true);
    };

    const getDefinitionData = (
        data: UpsellingRateEntityResponse
    ): FormUpsellingRateModel => ({
        ...data,
        commercialCategory: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory
        ) as CommercialCategorySelectFE,
        commercialSegmentId: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory.commercialSegment
        ) as SelectElementModelFE,
        dealer: {code: data.dealer, label: data.dealer},
        legalEntity: {code: data.legalEntity, label: data.legalEntity},
    });

    const upsellingRateMatrix = async (
        groupId: UpsellingRateEntityResponse["id"]
    ) => {
        try {
            return await getUpsellingRateMatrix(groupId);
        } catch (e) {
        }
    };

    const updateButtonClicked = async (data: UpsellingRateEntityResponse) => {
        const definitionData = getDefinitionData(data);
        const matrixData = await upsellingRateMatrix(data.id);
        const upsellingRateInUse: boolean = await getUpsellingRateInUse({upsellingRateId: data.id});

        setCreationForm({
            readOnly: false,
            inUse: upsellingRateInUse,
            upsellingRateId: data.id,
            definitionData,
            matrixData,
            step: 0,
            completed: [
                !!definitionData,
                Boolean(matrixData?.matrixRows?.length),
                Boolean(matrixData?.matrixRows?.length),
            ],
        });
        setIsCreationFormOpen(true);
    };

    const terminateButtonClicked = async (data: UpsellingRateEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmTerminate(data.id),
            message: t(
                "pricing:upsellingRate.dialogContents.terminateUpsellingRateInfo"
            ),
        });
    };

    const handleConfirmTerminate = async (id: number) => {
        await terminateUpsellingRate(id);
        setDialog(null);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    const deleteButtonClicked = (data: UpsellingRateEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmDelete(data.id),
            message: t(
                "pricing:upsellingRate.dialogContents.removeUpsellingRateInfo"
            ),
        });
    };

    const handleConfirmDelete = async (id: number) => {
        try {
            if (id) {
                await deleteUpsellingRate(id);
                setDialog(null);
                refresh();
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            }
        } catch (e) {
        }
    };
    /* TABLE ACTIONS - END */

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        setTestFilters({
            name: "",
            commercialCategoryId: null,
            commercialSegmentId: null,
            from: "",
            to: "",
            showOnlyActive: false,
            dealers: dealerOption || [],
            legalEntities: legalEntityOptions
        });
        handleTriggerFilters({
            name: "",
            commercialCategoryId: null,
            commercialSegmentId: null,
            from: "",
            to: "",
            showOnlyActive: false,
            dealers: dealerFilteredOptions,
            legalEntities: legalEntityOptions?.map(le => le.code as string) || [] // Estrai i codici con protezione
        });
    };

    const handleGetUpsellingRateList = () => {
        handleTriggerFilters({
            name: testFilters.name,
            commercialSegmentId:
                (
                    testFilters.commercialSegmentId as SelectElementModelFE
                )?.code?.toString() || "",
            commercialCategoryId:
                (
                    testFilters.commercialCategoryId as SelectElementModelFE
                )?.code?.toString() || "",
            from: testFilters.from
                ? dayjs(testFilters.from).toISOString()
                : testFilters.from,
            to: testFilters.to
                ? dayjs(testFilters.to).toISOString()
                : testFilters.to,
            showOnlyActive: testFilters.showOnlyActive,
            legalEntities: Array.isArray(testFilters.legalEntities) 
                ? (testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)
                : [],
            dealers: Array.isArray(testFilters.dealers)
                ? (testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)
                : dealerFilteredOptions || [],
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleGetUpsellingRateList();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const onDone = () => {
        setIsCreationFormOpen(false);
        setCreationForm(initialUpsellingRateCreation);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    const onClose = () => {
        creationForm.readOnly || refresh();
        setIsCreationFormOpen(false);
        setCreationForm(initialUpsellingRateCreation);
    };

    const createButtonClicked = async () => {
        setCreationForm({
            readOnly: false,
            inUse: false,
            definitionData: undefined,
            step: 0,
            completed: [false, false, false],
        });
        setIsCreationFormOpen(true);
    };

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_UPSELLING_RATE";
                        });
                    })
                        ? {onClick: createButtonClicked}
                        : undefined
                }
                activeSwitch={{
                    label: t("pricing:rentalPeriods.form.labels.showActive"),
                    handleChange: (checked: boolean) => {
                        setTestFilters({
                            ...testFilters,
                            showOnlyActive: checked,
                        });
                    },
                    value: testFilters.showOnlyActive as boolean,
                }}
            />
        ),
    };

    return (
        <Box>
            <SectionHeader
                title={t("translation:auths.SCOPE_PRC_VIEW_UPSELLING_RATE.name")}
            />

            <UpsellingRateFormContainer
                onDone={onDone}
                isOpen={isCreationFormOpen}
                onClose={onClose}
                refresh={refresh}
            />

            <UpsellingRateFilters
                handleGetUpsellingRateList={handleGetUpsellingRateList}
                handleClickResetSearch={handleTriggerResetFilters}
                testFilters={testFilters}
                setTestFilters={setTestFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        toolbar={toolbar}
                        defaultColDef={defaultColDef}
                        columnDefs={columnDefs}
                        pagination
                        localeText={agGridLocale}
                        tooltipShowDelay={0}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={setServerSideDatasource}
                    />
                    {dialog?.open && (
                        <DialogComponent
                            type={"info"}
                            onClickConfirm={dialog.onClickConfirm}
                            setOpen={() => setDialog(null)}
                            open={dialog.open}
                            message={dialog.message}
                        />
                    )}
                </Grid>
            </Grid>

            <Loading/>
        </Box>
    );
};

export default UpsellingRate;
